import React from "react";
import { useTranslation } from "react-i18next";
import { useAdminControls } from "../../../state/AdminControlsContext";
import { useAdminService } from "../../../data/adminService";
import { useAsyncData } from "../../../hooks/useAsyncData";
import { LineChartCard } from "./LineChartCard";
import { SourceBarCard } from "./SourceBarCard";
import { ReferralCard } from "./ReferralCard";
import "./DashboardCharts.css";

/**
 * Dashboard charts section (FE-3): two line charts (registrations, logins) and a
 * source-bar + referral-card row, driven by `adminDashboardSeries` via the
 * Topbar range. Owns its own fetch so a series error doesn't take down the KPI
 * grid / milestones above it. Mock-first until BE-3 lands (flip REACT_APP_USE_MOCK).
 */
export const DashboardCharts: React.FC = () => {
  const { t } = useTranslation();
  const { rangeVars } = useAdminControls();
  const service = useAdminService();

  const { data, loading, error, reload } = useAsyncData(
    () => service.getDashboardSeries(rangeVars),
    [service, rangeVars.range, rangeVars.from, rangeVars.to],
  );

  if (error) {
    return (
      <div className="lyra-error charts-error">
        <span>{t("dashboard.charts.error")}</span>
        <button className="range-btn range-btn--ghost" onClick={reload}>
          {t("dashboard.retry")}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="charts-row">
        <LineChartCard
          title={t("dashboard.charts.registrations")}
          points={data?.registrations ?? null}
          variant="accent"
          loading={loading}
        />
        <LineChartCard
          title={t("dashboard.charts.logins")}
          points={data?.logins ?? null}
          variant="warm"
          loading={loading}
        />
      </div>
      <div className="source-row">
        <SourceBarCard organic={data?.registrationSource.organic ?? null} loading={loading} />
        <ReferralCard />
      </div>
    </>
  );
};
